// GUIDED-FLOW: the in-memory twin of the guided-flow routes (`flows.*`), minimal but coherent.
// A run keeps only its progress; answers are read from the fake's real fields (the seat, its
// settings, the responsibilities, the seat's KPI set, the seat's budgets, the DE's "How I work")
// with the same engine the web renders with (`@companyos/domain-work/flows`). What the twin has
// no store for (the DE profile text, the invitation's access level, the sealed pay step) is kept
// on the run, the sealed values as "filled" only. The helper answers from the step's help text and
// suggests one value, never writing.
import {
  type Answers,
  type AnswerValue,
  type Audience,
  allSteps,
  COLLECT_FLOWS,
  type CollectSource,
  checkSave,
  collectProgress,
  DE_TEMPLATE_PREFILL,
  type FieldDef,
  type FlowDef,
  type FlowKey,
  findStep,
  flowDef,
  isAnswered,
  missingFields,
  progressLine,
  progressOf,
  roleNeeds,
  type Viewer,
} from "@companyos/domain-work/flows";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal, problem } from "./fake";
import { uid } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";
import type { InternalFetch } from "./fake-palette";
import type { Seat } from "./types";

type RunView = OperationOutput<"flows.get">;
type RunSummary = OperationOutput<"flows.send">;

type FakeRun = {
  id: string;
  company_id: string;
  flow_key: FlowKey;
  seat_id: string;
  assignee_membership_id: string | null;
  invitation_id: string | null;
  assignee_name: string | null;
  status: "open" | "completed" | "cancelled";
  saved_steps: string[];
  /** Answers the twin has no real field for (DE profile, access level, sealed "filled"). */
  kept: Record<string, AnswerValue>;
  template_key: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
};

export type FlowsDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: typeof problem;
  notFound: () => Response;
  now: () => Date;
  nextSeq: () => number;
  seatsOf: (p: Principal) => Seat[];
  internal: InternalFetch;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

async function rows<T>(res: Response): Promise<T[]> {
  if (!res.ok) return [];
  const body = (await res.json()) as { data?: T[] };
  return body.data ?? [];
}

export function createFlowsFake(d: FlowsDeps) {
  const runs: FakeRun[] = [];
  /** `flows.send` answers by company and Idempotency-Key. */
  const sent = new Map<string, RunSummary>();
  const stamp = () => d.now().toISOString();
  const myMembership = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;

  function viewerOf(p: Principal, run: FakeRun): Viewer & { list: Audience[] } {
    const list: Audience[] = [];
    if (p.manager) list.push("manager");
    if (p.roles.includes("owner")) list.push("owner");
    const mine = myMembership(p);
    if (mine && mine === run.assignee_membership_id) list.push("assignee");
    const access = run.kept.access_level;
    return {
      list,
      audiences: new Set(list),
      assigneeAccess:
        access === "admin" || access === "member" || access === "client_viewer" ? access : null,
    };
  }

  const settingAt = (seat: Seat, path: readonly string[]): unknown => {
    let v: unknown = seat.settings ?? {};
    for (const k of path) v = isRecord(v) ? v[k] : undefined;
    return v;
  };

  function setSetting(seat: Seat, path: readonly string[], value: AnswerValue) {
    const settings = { ...((seat.settings ?? {}) as Record<string, unknown>) };
    const [a, b] = path as [string, string | undefined];
    if (b === undefined) settings[a] = value;
    else settings[a] = { ...(isRecord(settings[a]) ? settings[a] : {}), [b]: value };
    (seat as { settings: unknown }).settings = settings;
  }

  async function linkedCount(p: Principal, run: FakeRun, f: FieldDef): Promise<number> {
    if (f.bind.kind !== "linked") return 0;
    const co = p.company.id;
    switch (f.bind.editor) {
      case "responsibilities":
        return (
          await rows(await d.internal(`/v1/companies/${co}/seats/${run.seat_id}/responsibilities`))
        ).length;
      case "kpis": {
        const res = await d.internal(`/v1/companies/${co}/seats/${run.seat_id}/metrics`);
        if (!res.ok) return 0;
        const body = (await res.json()) as { items?: unknown[]; data?: unknown[] };
        return (body.items ?? body.data ?? []).length;
      }
      case "budget":
        return (
          await rows(
            await d.internal(
              `/v1/companies/${co}/budgets?subject_kind=seat&subject_id=${run.seat_id}`,
            ),
          )
        ).length;
      case "how_i_work": {
        const de = await deOnSeat(p, run.seat_id);
        if (!de) return 0;
        const res = await d.internal(`/v1/companies/${co}/digital-employees/${de}`);
        if (!res.ok) return 0;
        const body = (await res.json()) as { how_i_work?: { text?: string | null } | null };
        return body.how_i_work?.text ? 1 : 0;
      }
      case "agent_policy": {
        // The seat's own rows (a class set to anything but inherit).
        const res = await d.internal(`/v1/companies/${co}/seats/${run.seat_id}/agent-policy`);
        if (!res.ok) return 0;
        const body = (await res.json()) as { classes?: { seat_mode?: string }[] };
        return (body.classes ?? []).filter((c) => c.seat_mode && c.seat_mode !== "inherit").length;
      }
      case "models": {
        const de = await deOnSeat(p, run.seat_id);
        if (!de) return 0;
        const res = await d.internal(
          `/v1/companies/${co}/digital-employees/${de}/template-override`,
        );
        if (!res.ok) return 0;
        const body = (await res.json()) as {
          override?: { role_model_map?: Record<string, unknown> | null } | null;
        };
        return Object.keys(body.override?.role_model_map ?? {}).length;
      }
      case "home_channel": {
        const de = await deOnSeat(p, run.seat_id);
        if (!de) return 0;
        const res = await d.internal(`/v1/companies/${co}/digital-employees/${de}/job`);
        if (!res.ok) return 0;
        const body = (await res.json()) as { home_channel_id?: string | null };
        return body.home_channel_id ? 1 : 0;
      }
    }
    return 0;
  }

  async function deOnSeat(p: Principal, seatId: string): Promise<string | null> {
    const list = await rows<{ membership_id: string; seat_id?: string | null; status?: string }>(
      await d.internal(`/v1/companies/${p.company.id}/digital-employees?limit=200`),
    );
    return list.find((x) => x.seat_id === seatId && x.status !== "retired")?.membership_id ?? null;
  }

  async function answersOf(p: Principal, run: FakeRun, def: FlowDef): Promise<Answers> {
    const seat = d.seatsOf(p).find((s) => s.id === run.seat_id);
    const out: Record<string, AnswerValue> = {};
    for (const step of allSteps(def))
      for (const f of step.fields) {
        const b = f.bind;
        let v: unknown = null;
        if (b.kind === "seat" && seat)
          v =
            b.column === "supervisor_seat_id"
              ? seat.supervisor_seat_id
              : b.column === "staff"
                ? seat.staff
                : seat[b.column];
        else if (b.kind === "seat_setting" && seat) v = settingAt(seat, b.path);
        else if (b.kind === "post_approval" && seat)
          v = seat.settings ? settingAt(seat, ["require_approval"]) === "message" : null;
        else if (b.kind === "lanes" && seat) v = settingAt(seat, ["lanes"]) ? true : null;
        else if (b.kind === "collect") v = seat ? settingAt(seat, ["collect", f.key]) : null;
        else if (b.kind === "linked") v = await linkedCount(p, run, f);
        else v = run.kept[f.key] ?? null;
        out[f.key] =
          typeof v === "string" ||
          typeof v === "number" ||
          typeof v === "boolean" ||
          (Array.isArray(v) && v.every((x) => typeof x === "string"))
            ? (v as AnswerValue)
            : null;
      }
    // The API's branching fact: the seat reports to nobody and has direct reports.
    const seats = d.seatsOf(p);
    out._top_of_chart =
      !!seat &&
      !seat.supervisor_seat_id &&
      seats.some((s) => s.supervisor_seat_id === seat.id && s.status !== "closed");
    return out;
  }

  function summaryOf(p: Principal, run: FakeRun, def: FlowDef, answers: Answers): RunSummary {
    const access = viewerOf(p, run).assigneeAccess;
    const progress = progressOf(def, answers, run.saved_steps, access);
    const seat = d.seatsOf(p).find((s) => s.id === run.seat_id);
    return {
      id: run.id,
      flow_key: run.flow_key,
      title: def.title,
      seat_id: run.seat_id,
      seat_title: seat?.title ?? "Seat",
      status: run.status,
      assignee_membership_id: run.assignee_membership_id,
      invitation_id: run.invitation_id,
      assignee_name: run.assignee_name,
      progress: {
        chapters_done: progress.chaptersDone,
        chapters_total: progress.chaptersTotal,
        percent: progress.percent,
        line: progressLine(def, progress),
      },
      created_at: run.created_at,
      updated_at: run.updated_at,
      completed_at: run.completed_at,
    };
  }

  async function viewOf(p: Principal, run: FakeRun): Promise<RunView> {
    const def = flowDef(run.flow_key) as FlowDef;
    const answers = await answersOf(p, run, def);
    const viewer = viewerOf(p, run);
    const seats = d.seatsOf(p);
    const roles = await rows<{ id: string; name: string }>(
      await d.internal(`/v1/companies/${p.company.id}/roles?limit=200`),
    );
    const progress = progressOf(def, answers, run.saved_steps, viewer.assigneeAccess);
    const seat = seats.find((s) => s.id === run.seat_id);
    const sup = seat?.supervisor_seat_id
      ? seats.find((s) => s.id === seat.supervisor_seat_id)
      : null;
    const prefill: Record<string, string> = {};
    const fromTemplate = run.template_key ? DE_TEMPLATE_PREFILL[run.template_key] : undefined;
    for (const [k, v] of Object.entries(fromTemplate ?? {}))
      if (answers[k] === null || answers[k] === undefined) prefill[k] = v;
    const sealed = new Set(
      allSteps(def)
        .filter((s) => s.secure)
        .flatMap((s) => s.fields.map((f) => f.key)),
    );
    return {
      ...summaryOf(p, run, def, answers),
      viewer: {
        audiences: viewer.list,
        assignee_access: viewer.assigneeAccess,
        can_cancel: p.manager && run.status === "open",
        can_open_sensitive: viewer.audiences.has("owner") || viewer.audiences.has("assignee"),
      },
      answers: Object.fromEntries(
        Object.entries(answers).map(([k, v]) => [
          k,
          sealed.has(k) ? (v === null ? null : true) : (v as RunView["answers"][string]),
        ]),
      ),
      saved_steps: [...run.saved_steps],
      missing: missingFields(def, answers, viewer.assigneeAccess).map((m) => ({
        ...m,
        fill: [...m.fill],
      })),
      options: {
        seats: seats
          .filter((s) => s.id !== run.seat_id && s.status !== "closed")
          .map((s) => ({ id: s.id, label: s.title })),
        roles: roles.map((r) => ({ id: r.id, label: r.name })),
      },
      // Completion over every answer (the caller's answers below are cut).
      chapters: progress.chapters.map((c) => ({ ...c, steps: c.steps.map((s) => ({ ...s })) })),
      lanes:
        run.flow_key === "de_onboarding"
          ? {
              escalate_to: sup ? { seat_id: sup.id, title: sup.title, holders: [] } : null,
              peers: seats
                .filter(
                  (s) =>
                    s.id !== run.seat_id &&
                    s.status !== "closed" &&
                    !!seat?.supervisor_seat_id &&
                    s.supervisor_seat_id === seat.supervisor_seat_id,
                )
                .map((s) => ({ seat_id: s.id, title: s.title })),
              confirmed_at:
                (seat && (settingAt(seat, ["lanes", "confirmed_at"]) as string)) || null,
            }
          : null,
      prefill,
      access_level_locked: run.invitation_id === null,
      de_membership_id: run.flow_key === "de_onboarding" ? await deOnSeat(p, run.seat_id) : null,
      // The twin keeps no file names: the panel shows "File" for each id.
      files: [],
      version: run.version,
    };
  }

  /** COLLECT-FLOWS: progress per person (latest run per seat and flow) and per role. */
  async function collection(p: Principal) {
    const latest = new Map<string, FakeRun>();
    for (const r of [...runs].reverse())
      if (
        r.company_id === p.company.id &&
        r.status !== "cancelled" &&
        r.flow_key.startsWith("collect_") &&
        !latest.has(`${r.seat_id}:${r.flow_key}`)
      )
        latest.set(`${r.seat_id}:${r.flow_key}`, r);
    const me = myMembership(p);
    const sources: CollectSource[] = [];
    const people = [];
    for (const r of [...latest.values()].reverse()) {
      const def = flowDef(r.flow_key) as FlowDef;
      const answers = await answersOf(p, r, def);
      sources.push({ def, seatId: r.seat_id, person: r.assignee_name, answers });
      if (!p.manager && r.assignee_membership_id !== me) continue;
      const pr = collectProgress(def, answers);
      people.push({
        run_id: r.id,
        flow_key: r.flow_key,
        title: def.title,
        seat_id: r.seat_id,
        seat_title: d.seatsOf(p).find((s) => s.id === r.seat_id)?.title ?? "Seat",
        person_name: r.assignee_name,
        status: r.status === "completed" ? "completed" : "open",
        total: pr.total,
        answered: pr.answered,
        percent: pr.percent,
        required_total: pr.requiredTotal,
        required_answered: pr.requiredAnswered,
        missing: pr.missing.map((m) => ({
          step: m.step,
          field: m.field,
          label: m.label,
          required: m.required,
          used_by: [...m.usedBy],
        })),
      });
    }
    const sent = new Set(sources.map((s) => s.def.key));
    return {
      people,
      roles: roleNeeds(sources).map((n) => ({
        role: n.role,
        total: n.total,
        answered: n.answered,
        status: n.status,
        missing: n.missing.map((m) => ({
          flow_key: m.flow,
          field: m.field,
          label: m.label,
          seat_id: m.seatId,
          person_name: m.person,
          required: m.required,
        })),
      })),
      unsent: COLLECT_FLOWS.map((x) => x.key).filter((k) => !sent.has(k)),
    };
  }

  const runIn = (p: Principal, id: string | undefined) => {
    const run = runs.find((r) => r.id === id && r.company_id === p.company.id);
    if (!run) return null;
    const mine = myMembership(p);
    return p.manager || (mine !== null && run.assignee_membership_id === mine) ? run : null;
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "flows.list": async ({ principal: p, url }) => {
      if (!p) return d.notFound();
      const mine = url.searchParams.get("mine") === "true" || !p.manager;
      const me = myMembership(p);
      const seat = url.searchParams.get("seat_id");
      const status = url.searchParams.get("status");
      const list = runs.filter(
        (r) =>
          r.company_id === p.company.id &&
          (!mine || (me !== null && r.assignee_membership_id === me)) &&
          (!seat || r.seat_id === seat) &&
          (!status || r.status === status),
      );
      const data: RunSummary[] = [];
      for (const r of list) {
        const def = flowDef(r.flow_key) as FlowDef;
        data.push(summaryOf(p, r, def, await answersOf(p, r, def)));
      }
      return d.json({ data });
    },
    "flows.send": async ({ principal: p, body, headers }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.problem(403, "forbidden", "Forbidden");
      // A retry with the same Idempotency-Key gets the first answer back.
      const idem = headers.get("Idempotency-Key");
      const replay = idem ? sent.get(`${p.company.id}:${idem}`) : undefined;
      if (replay) return d.json(replay, 201);
      const b = (isRecord(body) ? body : {}) as {
        flow_key?: string;
        seat_id?: string;
        membership_id?: string;
        invitation_id?: string;
        invite?: { email?: string; display_name?: string; roles?: string[] };
      };
      const def = b.flow_key ? flowDef(b.flow_key) : null;
      if (!def) return d.problem(422, "validation_failed", "Validation failed", "flow_key");
      if ([b.membership_id, b.invitation_id, b.invite].filter((x) => x !== undefined).length !== 1)
        return d.problem(
          422,
          "validation_failed",
          "Validation failed",
          "send to exactly one of membership_id, invitation_id and invite",
        );
      if (b.invite && def.key !== "seat_setup")
        return d.problem(422, "validation_failed", "Validation failed", "invite: seat_setup only");
      const seat = d.seatsOf(p).find((s) => s.id === b.seat_id);
      if (!seat) return d.problem(422, "validation_failed", "Validation failed", "seat_id");
      if (seat.status === "closed") return d.problem(409, "seat_closed", "Seat closed");
      const open = runs.find(
        (r) =>
          r.company_id === p.company.id &&
          r.seat_id === seat.id &&
          r.flow_key === def.key &&
          r.status === "open",
      );
      if (open) {
        const current = summaryOf(p, open, def, await answersOf(p, open, def));
        return d.problem(409, "conflict", "An open run exists", undefined, { current });
      }
      let template: string | null = null;
      if (def.key === "de_onboarding") {
        const de = await deOnSeat(p, seat.id);
        if (de) {
          const res = await d.internal(`/v1/companies/${p.company.id}/digital-employees/${de}`);
          if (res.ok)
            template = ((await res.json()) as { template_key?: string }).template_key ?? null;
        }
      }
      // `invite`: the invitation and the run in one write (the invitation through people.invite).
      let invitationId = b.invitation_id ?? null;
      let inviteRoles: string[] | null = null;
      if (b.invite) {
        const res = await d.internal(`/v1/companies/${p.company.id}/people`, {
          method: "POST",
          body: b.invite,
        });
        if (!res.ok) return res;
        const inv = (await res.json()) as { id: string; roles: string[] };
        invitationId = inv.id;
        inviteRoles = inv.roles;
      }
      const at = stamp();
      const run: FakeRun = {
        id: uid(0xf10, d.nextSeq()),
        company_id: p.company.id,
        flow_key: def.key,
        seat_id: seat.id,
        assignee_membership_id: b.membership_id ?? null,
        invitation_id: invitationId,
        assignee_name: b.invite?.display_name ?? null,
        status: "open",
        saved_steps: [],
        kept: invitationId ? { access_level: inviteRoles?.[0] ?? "member" } : {},
        template_key: template,
        version: 1,
        created_at: at,
        updated_at: at,
        completed_at: null,
      };
      runs.push(run);
      const out = summaryOf(p, run, def, await answersOf(p, run, def));
      if (idem) sent.set(`${p.company.id}:${idem}`, out);
      return d.json(out, 201);
    },
    "flows.collection": async ({ principal: p }) => {
      if (!p) return d.notFound();
      return d.json(await collection(p));
    },
    "flows.get": async ({ principal: p, params }) => {
      if (!p) return d.notFound();
      const run = runIn(p, params.id);
      return run ? d.json(await viewOf(p, run)) : d.notFound();
    },
    "flows.saveStep": async ({ principal: p, params, body }) => {
      if (!p) return d.notFound();
      const run = runIn(p, params.id);
      if (!run) return d.notFound();
      if (run.status !== "open") return d.problem(409, "conflict", "This flow is closed");
      const def = flowDef(run.flow_key) as FlowDef;
      const values = isRecord(body) && isRecord(body.values) ? body.values : {};
      const answers = await answersOf(p, run, def);
      const checked = checkSave(def, params.step ?? "", values, viewerOf(p, run), answers);
      if (!checked.ok)
        return d.problem(
          422,
          "validation_failed",
          "Validation failed",
          checked.errors[0]?.message,
          {
            errors: checked.errors.map((e) => ({
              path: `values.${e.field}`,
              code: "invalid",
              message: e.message,
            })),
          },
        );
      const step = findStep(def, params.step ?? "")?.step;
      const seat = d.seatsOf(p).find((s) => s.id === run.seat_id);
      for (const [key, value] of Object.entries(checked.values)) {
        const f = step?.fields.find((x) => x.key === key);
        if (!f) continue;
        const b = f.bind;
        if (b.kind === "seat" && seat) {
          if (b.column === "supervisor_seat_id")
            seat.supervisor_seat_id = (value as string) ?? null;
          else if (b.column === "staff") seat.staff = value === true;
          else if (typeof value === "string") seat[b.column] = value;
        } else if (b.kind === "seat_setting" && seat) setSetting(seat, b.path, value);
        else if (b.kind === "post_approval" && seat)
          setSetting(seat, ["require_approval"], value === true ? "message" : null);
        else if (b.kind === "lanes" && seat)
          setSetting(seat, ["lanes"], { confirmed_at: stamp() } as unknown as AnswerValue);
        else if (b.kind === "collect" && seat) setSetting(seat, ["collect", key], value);
        else run.kept[key] = b.kind === "secure" ? (value === null ? null : true) : value;
      }
      if (seat) seat.version += 1;
      if (!run.saved_steps.includes(params.step ?? "")) run.saved_steps.push(params.step ?? "");
      run.version += 1;
      run.updated_at = stamp();
      return d.json(await viewOf(p, run));
    },
    "flows.submit": async ({ principal: p, params }) => {
      if (!p) return d.notFound();
      const run = runIn(p, params.id);
      if (!run) return d.notFound();
      if (run.status !== "open") return d.problem(409, "conflict", "This flow is closed");
      const def = flowDef(run.flow_key) as FlowDef;
      const answers = await answersOf(p, run, def);
      const missing = missingFields(def, answers, viewerOf(p, run).assigneeAccess);
      // Refused while required fields are missing (as the API): 422, one error per field.
      if (missing.length)
        return d.problem(422, "validation_failed", "Required fields are missing", undefined, {
          errors: missing.map((m) => ({
            path: `${m.step}.${m.field}`,
            code: "required",
            message: `${m.label} (filled by ${m.fill.join(" or ")})`,
          })),
        });
      run.status = "completed";
      run.completed_at = stamp();
      run.updated_at = run.completed_at;
      run.version += 1;
      return d.json(await viewOf(p, run));
    },
    "flows.cancel": async ({ principal: p, params }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.problem(403, "forbidden", "Forbidden");
      const run = runIn(p, params.id);
      if (!run) return d.notFound();
      if (run.status !== "open") return d.problem(409, "conflict", "This flow is closed");
      run.status = "cancelled";
      run.updated_at = stamp();
      const def = flowDef(run.flow_key) as FlowDef;
      return d.json(summaryOf(p, run, def, await answersOf(p, run, def)));
    },
    "flows.helper": async ({ principal: p, params, body }) => {
      if (!p) return d.notFound();
      const run = runIn(p, params.id);
      if (!run) return d.notFound();
      const def = flowDef(run.flow_key) as FlowDef;
      const stepKey = isRecord(body) && typeof body.step_key === "string" ? body.step_key : "";
      const step = findStep(def, stepKey)?.step;
      if (!step) return d.problem(422, "validation_failed", "Validation failed", "step_key");
      const answers = await answersOf(p, run, def);
      const viewer = viewerOf(p, run);
      const target = step.secure
        ? undefined
        : step.fields.find(
            (f) =>
              f.fill.some((a) => viewer.audiences.has(a)) &&
              !isAnswered(f, answers[f.key]) &&
              ["text", "longtext", "number", "select", "time"].includes(f.type),
          );
      const suggestion = !target
        ? null
        : target.type === "number"
          ? {
              field: target.key,
              label: target.label,
              value: Math.min(target.max ?? 40, Math.max(target.min ?? 0, 40)),
            }
          : target.type === "select"
            ? {
                field: target.key,
                label: target.label,
                value: target.options?.[0]?.value ?? null,
              }
            : target.type === "time"
              ? { field: target.key, label: target.label, value: "09:00" }
              : { field: target.key, label: target.label, value: `${target.label} for this seat` };
      return d.json({
        answer: step.secure
          ? `${step.info} I can't see these values; fill them in the step.`
          : `${step.title}: ${step.info}`,
        suggestion,
        source: "help",
      });
    },
    "flows.sensitive": ({ principal: p, params }) => {
      if (!p) return d.notFound();
      const run = runIn(p, params.id);
      if (!run) return d.notFound();
      if (!p.roles.includes("owner") && run.assignee_membership_id !== myMembership(p))
        return d.problem(403, "forbidden", "Forbidden");
      // The twin never keeps the sealed values themselves.
      return d.json({ values: {} });
    },
  };

  /** After an invitation is accepted, its runs go to the new member (the token names it). */
  function onAccept(accept: Handler | undefined): ComposedHandler {
    return async (ctx) => {
      if (!accept) return d.notFound();
      const res = accept(ctx);
      if (!res.ok) return res;
      const token = isRecord(ctx.body) && typeof ctx.body.token === "string" ? ctx.body.token : "";
      const invitationId = token.split(".")[1];
      const clone = res.clone();
      const body = (await clone.json()) as { membership?: { id?: string; display_name?: string } };
      for (const r of runs)
        if (invitationId && r.invitation_id === invitationId) {
          r.assignee_membership_id = body.membership?.id ?? null;
          r.assignee_name = body.membership?.display_name ?? null;
        }
      return res;
    };
  }

  return { handlers, onAccept };
}
